import { chain } from '@leprecoins/effector';
import { createForm } from '@leprecoins/effector-forms';
import dayjs from 'dayjs';
import { combine, createEvent, createStore, sample } from 'effector';
import { createBlockVoucherModel } from 'features/block-voucher';
import { createDeleteVoucherModel } from 'features/delete-voucher';
import { createUnblockVoucherModel } from 'features/unblock-voucher';
import { createEditVoucherMutation, createGetVoucherQuery } from 'shared/api';
import { greaterZeroRule, notEmptyRule, notify } from 'shared/lib';
import { authorizedVoucherRoute, router } from 'shared/routing';

export type VoucherPageTabValues = 'info' | 'promocode';

const getVoucherQuery = createGetVoucherQuery();

const editVoucherMutation = createEditVoucherMutation();

const blockVoucherModel = createBlockVoucherModel();

const unblockVoucherModel = createUnblockVoucherModel();

const deleteVoucherModel = createDeleteVoucherModel();

const form = createForm({
	fields: {
		deposit: {
			init: '',
			rules: [notEmptyRule, greaterZeroRule],
		},
		wagering: {
			init: '',
			rules: [notEmptyRule, greaterZeroRule],
		},
	},
	validateOn: ['submit'],
	resetErrorsOnChange: true,
});

const $voucher = getVoucherQuery.$data;

const $voucherName = $voucher.map((voucher) =>
	voucher ? `Voucher at ${dayjs(voucher.createdAt).format('YYYY-MM-DD HH:mm:ss')}` : null,
);

const $currentTab = createStore<VoucherPageTabValues>('info');

const $pending = combine(
	{
		voucher: $voucher,
		pending: getVoucherQuery.$pending,
	},
	({ voucher, pending }) => !voucher && pending,
);

const changeTab = createEvent<VoucherPageTabValues | null>();

chain(changeTab, $currentTab);

sample({
	clock: authorizedVoucherRoute.opened,
	fn: ({ params }) => params.id,
	target: getVoucherQuery.start,
});

sample({
	clock: getVoucherQuery.finished.success,
	fn: ({ result }) => ({
		deposit: result.deposit.toString(),
		wagering: result.wagering.toString(),
	}),
	target: [form.setInitialValues, form.setValues],
});

sample({
	clock: form.submitted,
	source: { values: form.$values, voucher: $voucher },
	filter: ({ voucher }) => voucher !== null,
	fn: ({ values, voucher }) => ({
		id: voucher!.id,
		deposit: Number(values.deposit),
		wagering: Number(values.wagering),
		isLocked: voucher!.isLocked,
	}),
	target: editVoucherMutation.start,
});

notify(editVoucherMutation.finished.success, 'voucher-page.edit-success');

sample({
	clock: [
		editVoucherMutation.finished.success,
		blockVoucherModel.mutation.finished.success,
		unblockVoucherModel.mutation.finished.success,
	],
	source: $voucher,
	filter: Boolean,
	fn: ({ id }) => id,
	target: getVoucherQuery.start,
});

chain(deleteVoucherModel.mutation.finished.success, router.back);

sample({
	clock: authorizedVoucherRoute.closed,
	target: [form.reset, getVoucherQuery.reset, editVoucherMutation.reset],
});

export const voucherPageModel = {
	getVoucherQuery,
	editVoucherMutation,
	blockVoucherModel,
	unblockVoucherModel,
	deleteVoucherModel,
	form,
	$voucher,
	$voucherName,
	$currentTab,
	changeTab,
	$pending,
};
